import { useNavigate, useParams } from "@tanstack/react-router";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useOTRuleDetailQuery } from "../api/queries";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from "@devhop/ui";
import { ArrowLeft, BookmarkIcon, HelpCircle, Loader2, Pencil } from "lucide-react";
import { OTRuleDetailTour, useOTRuleDetailTour } from "../tour/OTRuleDetailTour";
import { formatDateLocal } from "@/shared/lib/date-time";

const DAY_TYPE_LABEL: Record<string, string> = {
  weekday: "ວັນທຳມະດາ",
  holiday: "ວັນພັກ",
};

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1 border-b border-border py-2 last:border-0">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm">{value ?? "—"}</span>
    </div>
  );
}

export function OTRuleDetailPage() {
  const { id } = useParams({ strict: false });
  const nav = useNavigate();
  const { data: rule, isLoading, error } = useOTRuleDetailQuery(id);
  const tour = useOTRuleDetailTour();

  if (isLoading || (!rule && !error)) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </Main>
      </>
    );
  }

  if (error || !rule) {
    return (
      <>
        <Header />
        <Main>
          <div className="mx-auto w-full max-w-4xl py-4">
            <p className="text-destructive">ບໍ່ພົບຂໍ້ກຳນົດ OT.</p>
            <Button variant="outline" className="mt-4" onClick={() => nav({ to: "/app/hr/overtime/rules", search: { offset: 0, limit: 20, dayType: undefined, isActive: undefined, search: undefined } })}>
              <ArrowLeft className="h-4 w-4" />
              ກັບຄືນ
            </Button>
          </div>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header />
      <Main>
        <OTRuleDetailTour run={tour.run} onCallback={tour.handleJoyrideCallback} />
        <div className="mb-6 flex items-center justify-between gap-2" data-tourid="ot-rule-detail-back">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-semibold text-2xl">ລາຍລະອຽດຂໍ້ກຳນົດ OT</h1>
              <Button
                variant="ghost"
                size="icon"
                className="size-7 rounded-full"
                onClick={tour.startTour}
                title="ເລີ່ມທົດສອບການນຳທາງ"
                aria-label="ເລີ່ມທົດສອບການນຳທາງ"
              >
                <HelpCircle className="size-5" />
              </Button>
            </div>
            <p className="flex items-center gap-2 text-muted-foreground text-sm">
              {rule.name}
              <Badge variant={rule.isActive ? "default" : "secondary"}>
                {rule.isActive ? "ໃຊ້ງານ" : "ປິດ"}
              </Badge>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => nav({ to: "/app/hr/overtime/rules", search: { offset: 0, limit: 20, sort: undefined, dayType: undefined, isActive: undefined, search: undefined } })}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              ກັບໄປລາຍການ
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => nav({ to: "/app/hr/overtime/rules/$id/edit", params: { id: rule.id } })}
              className="gap-2"
            >
              <Pencil className="h-4 w-4" />
              ແກ້ໄຂ
            </Button>
          </div>
        </div>

        <div className="mx-auto w-full max-w-4xl py-4">
          <Card data-tourid="ot-rule-detail-card">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <BookmarkIcon className="h-4 w-4" />
                {rule.name}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-0">
              <DetailRow label="ຊື່ຂໍ້ກຳນົດ" value={rule.name} />
              <DetailRow label="ປະເພດວັນ (dayType)" value={DAY_TYPE_LABEL[rule.dayType] ?? rule.dayType} />
              <DetailRow label="ອັດຕາຄູນ (rateMultiplier)" value={rule.rateMultiplier} />
              <DetailRow label="ມີຜົນຕັ້ງແຕ່ (effectiveFrom)" value={formatDateLocal(rule.effectiveFrom)} />
              <DetailRow
                label="ມີຜົນຈົນເຖິງ (effectiveTo)"
                value={rule.effectiveTo ? formatDateLocal(rule.effectiveTo) : "ຖາວອນ"}
              />
              <DetailRow label="ສະຖານະ" value={rule.isActive ? "ໃຊ້ງານ" : "ປິດ"} />
              <DetailRow label="ຄຳອະທິບາຍ" value={rule.description} />
            </CardContent>
          </Card>
        </div>
      </Main>
    </>
  );
}
